<script setup>
import {computed, nextTick, onBeforeUnmount, onMounted, ref} from 'vue'

const props = defineProps({
  top: false,
  right: false,
})

const isMobile = ref(false)
const isOpen = ref(false)
const triggerRef = ref(null)
const cardRef = ref(null)
const x = ref(0)
const y = ref(0)
let mediaQuery = null
let closeTimer = null
let rafId = 0
let resizeHandler = null
let scrollHandler = null
let outsideClickHandler = null
let overTrigger = false
let overTip = false

const showPortal = computed(() => isOpen.value)
const portalStyle = computed(() => {
  return {
    top: `${y.value}px`,
    left: `${x.value}px`,
  }
})

const syncViewport = () => {
  if (typeof window === 'undefined') return
  isMobile.value = window.matchMedia('(max-width: 1024px), (hover: none), (pointer: coarse)').matches
  if (!isMobile.value) {
    isOpen.value = false
  }
}

const clearCloseTimer = () => {
  if (closeTimer) {
    clearTimeout(closeTimer)
    closeTimer = null
  }
}

const positionCard = () => {
  if (!triggerRef.value || !cardRef.value) return

  const trigger = triggerRef.value.getBoundingClientRect()
  const card = cardRef.value.getBoundingClientRect()
  const margin = 8
  const offset = 4

  let left
  let top

  if (isMobile.value) {
    left = trigger.left + (trigger.width / 2) - (card.width / 2)
    top = trigger.top - card.height - offset

    if (top < margin) {
      top = trigger.bottom + offset
    }
  } else {
    left = props.right
      ? trigger.left
      : trigger.left + (trigger.width / 2) - (card.width / 2)

    top = props.top
      ? trigger.top - card.height - offset
      : trigger.bottom + offset
  }

  left = Math.min(Math.max(left, margin), window.innerWidth - margin - card.width)
  top = Math.min(Math.max(top, margin), window.innerHeight - margin - card.height)

  x.value = Math.round(left)
  y.value = Math.round(top)
}

const schedulePosition = () => {
  if (rafId) return
  rafId = requestAnimationFrame(() => {
    rafId = 0
    positionCard()
  })
}

const bindViewportListeners = () => {
  if (resizeHandler || scrollHandler) return

  resizeHandler = () => {
    if (isOpen.value) {
      schedulePosition()
    }
  }

  scrollHandler = () => {
    if (isOpen.value) {
      schedulePosition()
    }
  }

  window.addEventListener('resize', resizeHandler, {passive: true})
  window.addEventListener('scroll', scrollHandler, {capture: true, passive: true})
}

const unbindViewportListeners = () => {
  if (resizeHandler) {
    window.removeEventListener('resize', resizeHandler)
    resizeHandler = null
  }

  if (scrollHandler) {
    window.removeEventListener('scroll', scrollHandler, true)
    scrollHandler = null
  }
}

const bindOutsideClick = () => {
  if (outsideClickHandler) return

  outsideClickHandler = (event) => {
    const trigger = triggerRef.value
    const card = cardRef.value
    const target = event.target

    if (trigger?.contains(target) || card?.contains(target)) {
      return
    }

    closeHelp()
  }

  document.addEventListener('click', outsideClickHandler, true)
}

const unbindOutsideClick = () => {
  if (outsideClickHandler) {
    document.removeEventListener('click', outsideClickHandler, true)
    outsideClickHandler = null
  }
}

const openHelp = async () => {
  clearCloseTimer()
  if (isOpen.value) {
    schedulePosition()
    return
  }

  isOpen.value = true
  await nextTick()
  positionCard()
  bindViewportListeners()
  bindOutsideClick()
}

const toggleHelp = () => {
  if (!isMobile.value) return openHelp()
  if (isOpen.value) {
    closeHelp()
    return
  }
  openHelp()
}

const closeHelp = () => {
  isOpen.value = false
  clearCloseTimer()
  unbindViewportListeners()
  unbindOutsideClick()
  if (rafId) {
    cancelAnimationFrame(rafId)
    rafId = 0
  }
}

const scheduleClose = () => {
  clearCloseTimer()
  closeTimer = setTimeout(() => {
    if (!overTrigger && !overTip) {
      closeHelp()
    }
  }, 120)
}

const onEnterTrigger = () => {
  overTrigger = true
  if (!isMobile.value) {
    openHelp()
  }
}

const onLeaveTrigger = () => {
  overTrigger = false
  if (!isMobile.value) {
    scheduleClose()
  }
}

const onEnterTip = () => {
  overTip = true
  clearCloseTimer()
}

const onLeaveTip = () => {
  overTip = false
  if (!isMobile.value) {
    scheduleClose()
  }
}

onMounted(() => {
  mediaQuery = window.matchMedia('(max-width: 1024px), (hover: none), (pointer: coarse)')
  syncViewport()
  mediaQuery.addEventListener('change', syncViewport)
})

onBeforeUnmount(() => {
  clearCloseTimer()
  unbindViewportListeners()
  unbindOutsideClick()
  if (rafId) {
    cancelAnimationFrame(rafId)
  }
  mediaQuery?.removeEventListener('change', syncViewport)
})
</script>

<template>
  <div
    class="tip spill-tip"
    @mouseenter="onEnterTrigger"
    @mouseleave="onLeaveTrigger"
  >
    <button
      ref="triggerRef"
      type="button"
      class="tip-trigger"
      @click.stop.prevent="toggleHelp"
      @focus="onEnterTrigger"
      @blur="onLeaveTrigger"
    >
      <img alt="Question" src="/images/help/question.png">
    </button>

    <Teleport to="body">
      <template v-if="showPortal">
        <div
          class="help-portal"
          :class="{'help-portal-mobile': isMobile}"
          :style="portalStyle"
          @click="isMobile ? closeHelp() : null"
        >
          <div
            ref="cardRef"
            class="help-card"
            @mouseenter="onEnterTip"
            @mouseleave="onLeaveTip"
            @click.stop
          >
            <slot/>
          </div>
        </div>
      </template>
    </Teleport>
  </div>
</template>

<style scoped>
img {
  width: 14px;
  height: 14px;
  display: flex;
}

.tip {
  position: relative;
}

.tip-trigger {
  display: flex;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.help-portal {
  position: fixed;
  z-index: 10000000000;
  isolation: isolate;
  pointer-events: auto;
}

.help-portal-mobile {
  background: transparent;
}

.help-card {
  display: block;
  width: fit-content;
  max-width: 256px;
  box-sizing: border-box;
  white-space: normal;
  overflow-wrap: break-word;
  color: #fff;
  padding: 5px 10px;
  text-align: center;
  line-height: 16px;
  font-size: 11px;
  font-weight: 400;
  border-radius: 4px;
  background: rgba(0, 0, 0, .86);
}

.help-portal-mobile .help-card {
  width: fit-content;
  max-width: 256px;
  padding: 8px 12px;
}

.help-card:deep(a) {
  color: white;
  text-decoration: underline;
}
</style>
